import { useReduxState } from 'hooks';
import { useFormState } from 'informed';
import { Alert } from 'components/common';
import {
  Checkbox,
  FormGroup,
  FormSection,
  Select
} from 'components/common/form';

interface Props {
  isSiteSettingsView: boolean;
}

const SupervisorSummaryEmailSettings = ({ isSiteSettingsView }: Props) => {
  const formState = useFormState();

  const { messages, serverConfig } = useReduxState();

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  const summaryOptions = [
    ...(coursesVisible
      ? [
          {
            label: messages.notificationsTextCourseCompletion,
            value: 'courseCompletions'
          }
        ]
      : []),
    ...(pathsVisible
      ? [
          {
            label: messages.notificationsTextPathCompletion,
            value: 'pathCompletions'
          }
        ]
      : []),
    ...(achievementsVisible
      ? [
          {
            label: messages.notificationsTextAchievementCompletion,
            value: 'achievements'
          }
        ]
      : []),
    { label: messages.notificationsTextNewCompetencies, value: 'competencies' },
    ...(coursesVisible || pathsVisible
      ? [
          {
            label: messages.myTeamSettingsApproachingDeadline,
            value: 'approachingDeadlines'
          }
        ]
      : []),
    ...(coursesVisible || achievementsVisible
      ? [
          {
            label: messages.completionsExpiring,
            value: 'expiringCompletions'
          }
        ]
      : []),
    ...(coursesVisible || achievementsVisible
      ? [
          {
            label: messages.completionsExpired,
            value: 'expiredCompletions'
          }
        ]
      : []),
    ...(achievementsVisible
      ? [
          {
            label: messages.achievementAccessRequestPending,
            value: 'pendingAchievementAccessRequests'
          }
        ]
      : [])
  ];

  const isEnabledFormValue = isSiteSettingsView
    ? formState.values?.supervisorSummaryEmailEnabled
    : formState.values?.enableSummaryEmail;

  return (
    <FormSection>
      {serverConfig.supervisorSummaryEmailEnabled && !isSiteSettingsView ? (
        <Alert
          style={{ marginBottom: 15 }}
          text={messages.settingsSummaryEmailSomeOptionsAreEnforced}
          type="warning-light"
        />
      ) : null}

      <FormGroup
        label={messages.settingsSummaryEmail}
        labelHelpText={messages.settingsSummaryEmailHelpText}
      >
        <Checkbox
          disabled={
            serverConfig.supervisorSummaryEmailEnabled && !isSiteSettingsView
          }
          id="enable-summmary-email"
          field={
            isSiteSettingsView
              ? 'supervisorSummaryEmailEnabled'
              : 'enableSummaryEmail'
          }
          label={messages.settingsSummaryEmailSubscribe}
          style={{ marginTop: 5 }}
        />
      </FormGroup>

      <FormGroup label={messages.settingsSummaryEmailFrequency}>
        <Select
          disabled={
            (serverConfig.supervisorSummaryEmailEnabled &&
              !isSiteSettingsView) ||
            !isEnabledFormValue
          }
          id="summary-email-frequency"
          field={
            isSiteSettingsView
              ? 'supervisorSummaryEmailFrequency'
              : 'summaryEmailFrequency'
          }
          options={[
            {
              label: messages.settingsSummaryEmailFrequencyDay,
              value: 'day'
            },
            {
              label: messages.settingsSummaryEmailFrequencyWeek,
              value: 'week'
            },
            {
              label: messages.settingsSummaryEmailFrequencyMonth,
              value: 'month'
            }
          ]}
          style={{ maxWidth: 250 }}
        />
      </FormGroup>

      <FormGroup label={messages.settingsSummaryEmailChooseEvents}>
        {summaryOptions.map((option) => (
          <Checkbox
            key={option.value}
            disabled={
              (serverConfig.supervisorSummaryEmailEnabled
                ? serverConfig.supervisorSummaryEmailOptions[option.value] &&
                  !isSiteSettingsView
                : false) || !isEnabledFormValue
            }
            id={`summary-email-option-${option.value}`}
            field={`${
              isSiteSettingsView
                ? 'supervisorSummaryEmailOptions'
                : 'summaryEmailOptions'
            }.${option.value}`}
            label={option.label}
            style={{ marginTop: 5 }}
          />
        ))}
      </FormGroup>
    </FormSection>
  );
};

export default SupervisorSummaryEmailSettings;
